import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Checkbox } from '@pkg/components';
import { DepButton, DepInput } from '@pkg/components/deprecated';
import { useAppDispatch, useAppSelector, useUpdateUser } from '@pkg/hooks';

import { Events } from '@app/client/common/structs/events/events';
import { validEmail } from '@app/helpers';
import SystemRedirect, { RedirectType } from '@app/helpers/classes/Redirect';
import { AUTH_SIGN_IN } from '@app/routes/paths/auth';
import { RoutingSearchParams } from '@app/routes/types';
import { magicLink, setMagicLinkEmail, useLogout } from '@app/store/actions/auth';
import { setError } from '@app/store/actions/error';
import { registerEvent } from '@app/store/actions/events';
import { addNotification } from '@app/store/actions/notification';
import { type ErrorState } from '@app/store/types/error';

import { Typography } from '../../DesignSystem';
import PasswordInput from '../../Input/PasswordInput';

const WaitListEmailForm = () => {
    const { t } = useTranslation(['waitlist', 'account']);
    const userId = useAppSelector((rootState) => rootState.user?.userAccount?.user?.id);
    const [weakPassword, setWeakPassword] = useState(false);
    const [isSubmitted, setSubmitted] = useState(false);
    const [subscribed, setSubscribed] = useState(false);
    const [partnerKey, setPartnerKey] = useState('');

    const [isInvalid, setInvalid] = useState(false);
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const { mutateAsync: updateUser } = useUpdateUser();

    const storedRedirect = SystemRedirect.getRedirect(RedirectType.Default) || '';
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const locationState = useLocation().state;

    useEffect(() => {
        const walletKey = locationState?.walletLinkCreation;
        setPartnerKey(walletKey);
    }, [locationState?.walletLinkCreation]);

    const logout = useLogout();
    const goToSignIn = useCallback(() => {
        const partnerParam = partnerKey ? `?${RoutingSearchParams.PartnerKey}=${partnerKey}` : '';
        const storedWithParam =
            !!storedRedirect && partnerParam && storedRedirect.includes(partnerParam)
                ? storedRedirect
                : `${storedRedirect}${partnerParam}`;

        const redirectPath = storedWithParam ? storedWithParam : `/checkout${partnerParam}`;

        logout(redirectPath);

        if (userId) {
            localStorage.setItem('draftUserId', userId);
        }
        navigate(AUTH_SIGN_IN, { state: { partnerKey: partnerKey } });
    }, [partnerKey, storedRedirect, logout, userId, navigate]);

    const handleMagicLink = useCallback(async () => {
        const path = partnerKey ? `/checkout?${RoutingSearchParams.PartnerKey}=${partnerKey}` : '/checkout';
        const magicLinkResponse = await dispatch(magicLink(email.trim(), path));
        try {
            if (magicLinkResponse.ok && magicLinkResponse.value) {
                dispatch(setMagicLinkEmail(email));
                navigate(`/new-user-confirmation`, { state: { partnerKey: partnerKey } });
            }
            if (!magicLinkResponse.ok && magicLinkResponse.error?.detail) {
                dispatch(addNotification({ message: magicLinkResponse?.error.detail, severity: 'alert' }));
            }
        } catch (e) {
            dispatch(setError({ ...(e as ErrorState), source: 'Waitlist magic link' }));
        }
    }, [dispatch, email, navigate, partnerKey]);

    const handleUpdateCurrentUser = useCallback(async () => {
        updateUser({ value: { email, password, subscribed }, dispatch })
            .then((userResponse) => {
                if (userResponse?.ok) {
                    registerEvent(Events.user_signup);
                    handleMagicLink();
                    return;
                }
                if (!userResponse.ok) {
                    if (userResponse?.fields?.['email']) {
                        dispatch(
                            addNotification({
                                message: t('waitlist_alert_unique'),
                                severity: 'error',
                                duration: 5000,
                            }),
                        );
                    }
                    if (userResponse?.fields?.['password']) {
                        dispatch(
                            addNotification({
                                message: t('waitlist_alert_password'),
                                severity: 'error',
                                duration: 5000,
                            }),
                        );
                    }
                }
            })
            .catch((e) => {
                dispatch(
                    addNotification({
                        message: e || t('waitlist_email_failed_confirm'),
                        severity: 'error',
                    }),
                );
            });
    }, [dispatch, email, handleMagicLink, password, subscribed, t, updateUser]);

    const handleSubmit = useCallback(() => {
        setSubmitted(true);
        if (!validEmail(email)) {
            setInvalid(true);
            return;
        }
        if (password.length < 8) {
            setWeakPassword(true);
            return;
        }
        setLoading(true);
        handleUpdateCurrentUser().finally(() => {
            setLoading(false);
        });
    }, [email, handleUpdateCurrentUser, password.length]);

    useEffect(() => {
        if (!isSubmitted) {
            return;
        }
        setInvalid(!validEmail(email));
    }, [email, isSubmitted]);

    useEffect(() => {
        if (!isSubmitted) {
            return;
        }
        setWeakPassword(password.length < 8);
    }, [password, isSubmitted]);

    return (
        <>
            <div className="waitlist-email__input email">
                <DepInput
                    autoComplete="email"
                    label={t('common:email')}
                    type="email"
                    isInvalid={isInvalid}
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
            </div>
            <div className="waitlist-email__input">
                <PasswordInput
                    isInvalid={weakPassword}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    errorLabel={t('waitlist_alert_password')}
                    autoFocus={false}
                />
            </div>
            <div className="waitlist-email__optin">
                <Checkbox
                    id="checkbox-optin"
                    onChange={(e) => {
                        setSubscribed(e.target.checked);
                    }}
                    checked={subscribed}
                    wrapperClass="waitlist-email__optin-check"
                >
                    <Typography type="paragraph" size={16}>
                        {t('waitlist_optin')}
                    </Typography>
                </Checkbox>
            </div>
            <div className="waitlist-email__btn">
                <DepButton size="large" onClick={handleSubmit} loading={loading}>
                    {t('waitlist_email_cta')}
                </DepButton>
            </div>
            <Typography variant="div" type="label" classes={['waitlist-email__link']}>
                <span onClick={goToSignIn}>{t('waitlist_email_existing')}</span>
            </Typography>
        </>
    );
};

export default WaitListEmailForm;
